/**
 * Module for communicating with Sennheiser headsets
 * - will use Sennheiser Epos Connect over websocket wss://127.0.0.1:41088/ or fallback to WebHID in Chrome
 */

import { useSettingsStore } from '@/stores/Settings'
import globalEvents from '@/utilities/GlobalEvents'
import SennHeiserEposApi from './SennheiserEposAPI'
import { useMediaDevicesStore } from '@/stores/MediaDevices'
import logger from '@/utilities/Logger'
import { MediaDeviceType, type MediaDevice } from '@/stores/MediaDevices.types'
import { ref, type Ref } from 'vue'

export default class Sennheiser {
	static #instance: Sennheiser // NOSONAR

	use: Ref<boolean> = ref(false)

	useDeviceName: Ref<string | undefined> = ref(undefined)

	private api?: SennHeiserEposApi = undefined

	eventCallback: (event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD') => void = () => { /** */ }

	private constructor() {
		logger.debug('Sennheiser: Constructor')

		globalEvents.on('MediaDeviceChange', device => {
			const mediaDevices = useMediaDevicesStore()

			if (device.kind === MediaDeviceType.audioinput &&
				(mediaDevices.microphoneDevice?.label.toLowerCase().includes('sennheiser') || mediaDevices.microphoneDevice?.label.toLowerCase().includes('epos'))) {
				this.init()
			} else {
				this.use.value = false
				this.useDeviceName.value = undefined
				if (this.api) {
					this.api.terminate()
					this.api = undefined
				}
				logger.debug('Sennheiser: Selected device is not Sennheiser - terminating')
			}
		})

		globalEvents.on('MediaDeviceSelected', data => {
			const mediaDevices = useMediaDevicesStore()

			if (data.type === MediaDeviceType.audioinput && (
				mediaDevices.microphoneDevice?.label.toLowerCase().includes('sennheiser') || mediaDevices.microphoneDevice?.label.toLowerCase().includes('epos'))) {
				this.init()
			} else {
				this.use.value = false
				this.useDeviceName.value = undefined
				if (this.api) {
					this.api.terminate()
					this.api = undefined
				}
				logger.debug('Sennheiser: Selected device is not Sennheiser - terminating')
			}
		})

		globalEvents.on('StatusRequest', () => {
			globalEvents.emit('StatusReport', { key: 'sennheiser', value: this.use.value })
			if (this.use.value) {
				globalEvents.emit('StatusReport', { key: 'sennheiserDevice', value: this.useDeviceName.value as string })
			}
		})
	}

	/** Get the Sennheiser singleton instance */
	static get instance() {
		return Sennheiser.#instance
	}

	/** Create the Sennheiser singleton instance and return it */
	static create(eventCallback: (event: 'ANSWER' | 'HANGUP' | 'REJECT' | 'HOLD' | 'UNHOLD') => void) {
		if (!Sennheiser.#instance) {
			Sennheiser.#instance = new Sennheiser()
		}
		Sennheiser.#instance.eventCallback = eventCallback
		return Sennheiser.#instance
	}

	// #region Init

	/** Setup what needs to be setup to initialize the headset device */
	private async init() {
		logger.info('Sennheiser: Initializing')
		this.use.value = false
		this.useDeviceName.value = undefined

		const mediaDevices = useMediaDevicesStore()

		await mediaDevices.ready()

		if (mediaDevices.microphoneDeviceId === 'default') {
			logger.info('Sennheiser: No device specified - initialization cancelled')
			return
		}

		if (mediaDevices.microphoneDevice?.label.toLowerCase().includes('sennheiser') || mediaDevices.microphoneDevice?.label.toLowerCase().includes('epos')) {
			return this.initBrowser(mediaDevices.microphoneDevice)
		} else {
			logger.info('Sennheiser: Selected audio device is not from Sennheiser - Initialization cancelled')
		}
	}

	/**
	 * Initialize for browser and app based usage
	 * @param device Audio device we want to control
	 */
	private async initBrowser(device: MediaDevice) {
		logger.info(`Sennheiser: InitBrowser: Initializing - looking for ${device.label} with ID ${device.productId}`)
		if (!this.api) {
			const ss = useSettingsStore()

			this.api = new SennHeiserEposApi('TelecomX', ss.runtime.appName, ss.runtime.version, true)

			this.api.addEventListener('answer', () => {
				logger.info('Sennheiser: User pressed answer')
				this.eventCallback('ANSWER')
			})

			this.api.addEventListener('reject', () => {
				logger.info('Sennheiser: User pressed reject')
				this.eventCallback('REJECT')
			})

			this.api.addEventListener('hangup', () => {
				logger.info('Sennheiser: User pressed hangup')
				this.eventCallback('HANGUP')
			})

			this.api.addEventListener('hold', () => {
				logger.info('Sennheiser: User put call on hold')
				this.eventCallback('HOLD')
			})

			this.api.addEventListener('resume', () => {
				logger.info('Sennheiser: User resumed call')
				this.eventCallback('UNHOLD')
			})

			this.api.addEventListener('status', data => {
				const state = (data as unknown as { detail: { state: string } }).detail.state

				logger.info(`Sennheiser: got event: ${state}`)
				switch (state) {
					case 'CONNECTING':
						break
					case 'RECONNECTING':
						this.use.value = false
						break
					case 'TERMINATED':
						this.use.value = false
						break
					case 'DISCONNECTED':
						this.use.value = false
						break
					case 'CONNECTED':
						this.use.value = true
						// this.api?.selectDevice(device.label)
						break
				}
			})

			this.api.addEventListener('headset', data => {
				const detail = (data as unknown as { detail: { type: string, name: string } }).detail

				if (detail.type === 'connected') {
					this.useDeviceName.value = detail.name
					logger.info('Sennheiser: Event: Headset connected - ' + detail.name)
				}
				if (detail.type === 'disconnected') {
					logger.info('Sennheiser: Event: Headset disconnected - ' + detail.name)
				}
			})
		}
	}

	// #endregion

	// #region Actions - public

	inboundStart(callId: string) {
		logger.info('Sennheiser: Telling headset inbound call started')
		this.api?.inboundStart(callId)
	}

	offHook(callId: string) {
		logger.info('Sennheiser: Telling headset call was answered')
		this.api?.offHook(callId)
	}

	inboundReject(callId: string) {
		logger.info('Sennheiser: Telling headset inbound call was rejected')
		this.api?.inboundReject(callId)
	}

	outboundStart(callId: string) {
		logger.info('Sennheiser: Telling headset outbound call has started')
		this.api?.outboundStart(callId)
	}

	outboundReject(callId: string) {
		logger.info('Sennheiser: Telling headset outbound call was rejected')
		this.api?.outboundReject(callId)
	}

	hangup(callId: string) {
		logger.info('Sennheiser: Telling headset to hangup')
		setTimeout(() => {
			this.api?.hangup(callId)
		}, 500)
	}

	hold(callId: string) {
		logger.info('Sennheiser: Telling headset to go on hold')
		this.api?.hold(callId)
	}

	unhold(callId: string) {
		logger.info('Sennheiser: Telling headset to go off hold')
		this.api?.unhold(callId)
	}

	async reInit() {
		await this.api?.terminate()
		await this.init()
	}

	// #endregion
}
